import { getProductConfigList } from '@/apis/config';
import {
  calculatePremium,
  calculateTotalPremium,
  getTravelPlanPrice,
  getTravelPlanPriceBreakdown,
} from '@/apis/product';
import { createTravelProposal, retrieveTravelProposal } from '@/apis/proposal';
import { AREA_ID_DOMESTIC } from '@/constants';
import { AddOn, FamilyCount, FlightType, PlanClass, PlanType } from '@/enums';
import {
  AddonCoverageState,
  UpdateAddonDetailStateRequest,
} from '@/types/AddonCoverage';
import { ApiResponseWrapper } from '@/types/Common/Wrapper';
import { FlightState, UpdateFlightDetailStateRequest } from '@/types/Flight';
import {
  BankAccountDetailData,
  PlanSummaryState,
  QuotationData,
} from '@/types/PlanSummary';
import { PriceBreakdownResponse, PriceDetailData } from '@/types/PriceDetail';
import { ProductConfigResponse } from '@/types/ProductConfig';
import { ProposalResponse } from '@/types/Proposal';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import format from 'date-fns/format';

const initialState: PlanSummaryState = {
  loading: false,
  planClass: 0,
  totalPremium: 0,
  discountPremium: 0,
  readyForPayment: false,
  declaration: false,
  marketingConsent: false,
  addonCoverages: {
    adventurousActivities: {
      isSelected: false,
    },
    covid19: {
      isSelected: false,
    },
    flightDelay: {
      isSelected: false,
    },
  },
  quotationDetail: undefined,
  priceBreakdown: undefined,
  flightDetails: [
    {
      departure: {
        flightDate: '',
        flightNumber: '',
      },
      return: {
        flightDate: '',
        flightNumber: '',
      },
    },
  ],
  bankAccountDetail: {
    accountHolder: '',
    accountNo: '',
    bankId: 0,
  },
  proposalData: undefined,
  payMode: '',
  encodedOrderId: '',
};

export const planSummarySlice = createSlice({
  name: 'planSummary',
  initialState: initialState,
  reducers: {
    updateTripType: (state, action: PayloadAction<number>) => {
      state.tripType = action.payload;
    },
    updateSelectedPricingPlan: (
      state,
      action: PayloadAction<PriceDetailData>,
    ) => {
      state.priceDetail = action.payload;
      state.planClass = action.payload.classId;
      calculateTotalPremium();
    },
    updateAddonDetail: (
      state,
      action: PayloadAction<UpdateAddonDetailStateRequest>,
    ) => {
      state.addonCoverages[
        action.payload.addonType as keyof AddonCoverageState
      ].isSelected = action.payload.isSelected;
      calculateTotalPremium();
    },
    updateQuotationDetail: (state, action: PayloadAction<QuotationData>) => {
      if (state.quotationDetail == undefined) {
        state.planClass =
          action.payload.areaId == AREA_ID_DOMESTIC ? 1 : PlanClass.MostPopular;
      } else {
        if (action.payload.areaId != AREA_ID_DOMESTIC) {
          state.planClass =
            state.quotationDetail.areaId == action.payload.areaId
              ? state.planClass
              : PlanClass.MostPopular;
        } else {
          state.planClass = 1;
        }
      }
      state.quotationDetail = action.payload;
    },
    updateFlightDetail: (
      state,
      action: PayloadAction<UpdateFlightDetailStateRequest>,
    ) => {
      const payload: FlightState = {
        departure:
          action.payload.direction === FlightType.Departure
            ? { ...action.payload.flightDetail }
            : state.flightDetails?.[0]?.departure ?? {
                flightDate: '',
                flightNumber: '',
              },

        return:
          action.payload.direction === FlightType.Return
            ? { ...action.payload.flightDetail }
            : state.flightDetails?.[0]?.return ?? {
                flightDate: '',
                flightNumber: '',
              },
      };

      state.flightDetails = state.flightDetails
        ? state.flightDetails.map((flight, index) =>
            index === 0 ? { ...flight, ...payload } : flight,
          )
        : [payload];
    },
    updateFlightDetails: (state, action: PayloadAction<FlightState[]>) => {
      state.flightDetails = action.payload;
    },
    updateDeclaration: (state, action: PayloadAction<boolean>) => {
      state.declaration = action.payload;
    },
    updateTermsAndCondition: (state, action: PayloadAction<boolean>) => {
      state.termsAndCondition = action.payload;
    },
    updateMarketingConsent: (state, action: PayloadAction<boolean>) => {
      state.marketingConsent = action.payload;
    },
    updateBankAccountDetail: (
      state,
      action: PayloadAction<BankAccountDetailData>,
    ) => {
      state.bankAccountDetail = action.payload;
    },
    setAsReadyForPayment: (state) => {
      state.readyForPayment = true;
    },
    mapProposalDataToPlanSummary: (
      state,
      action: PayloadAction<ProposalResponse>,
    ) => {
      const data = action.payload;

      state.readyForPayment = true;
      state.planClass = data.planClass;

      // Quotation detail.
      state.quotationDetail = {
        tripType: data.tripType,
        areaId: data.area,
        dateRange: {
          from: data.startDate ? new Date(data.startDate) : undefined,
          to: data.endDate ? new Date(data.endDate) : undefined,
        },
        planType: data.planType,
        travelStartDate: data.startDate
          ? format(new Date(data.startDate), 'dd/MM/yyyy')
          : '',
        travelEndDate: data.endDate
          ? format(new Date(data.endDate), 'dd/MM/yyyy')
          : '',
        adultCount: data.adultCount,
        childCount: data.childCount,
        seniorCount: 0,
      };

      // Price breakdown.
      state.priceBreakdown = {
        addOn: data.addOnList.map((x) => {
          return {
            addOnId: x.addOnId,
            riskCode: '',
            addOnRate: x.totalPremium,
            addOnCode: x.code,
            addOnDescription: x.description,
            addOnLongDescription: '',
            planClass: 0,
          };
        }),
        plan: {
          planClass: data.planClass,
          planClassCode: data.planClassName,
          planClassDescription: data.planClassDescription,
          premiumRate: data.premium,
        },
        premium: data.premium,
        premiumTax: data.premiumTax,
        basicPremium: data.basicPremium,
        premiumTaxRate: data.premiumTaxRate,
        serviceTax: data.serviceTax,
        discountAmount: data.discountAmount,
        netPremium: data.netPremium,
        taxAmount: data.taxAmount,
        taxRate: data.taxRate,
        stampDuty: data.stampDuty,
        totalPremium: data.totalPremium,
      };

      // Addon.
      state.addonCoverages.covid19.isSelected =
        data.addOnList.find((x) => x.addOnId == AddOn.Covid) != undefined;
      state.addonCoverages.adventurousActivities.isSelected =
        data.addOnList.find((x) => x.addOnId == AddOn.Adventurous) != undefined;

      // Flight detail.
      state.flight = {
        departure: {
          flightDate: data.departureDate
            ? format(new Date(data.departureDate), 'dd/MM/yyyy')
            : '',
          flightNumber: data.departureFlightNumber,
        },
        return: {
          flightDate: data.returnDate
            ? format(new Date(data.returnDate), 'dd/MM/yyyy')
            : '',
          flightNumber: data.returnFlightNumber,
        },
      };

      // Bank detail.
      state.bankAccountDetail = {
        bankId: data.bankId,
        accountHolder: data.accountHolder,
        accountNo: data.accountNo,
      };
    },
    adultCountIncrement: (state) => {
      if (
        state.quotationDetail?.planType == PlanType.Family &&
        state.quotationDetail?.adultCount < FamilyCount.MAX_ADULT
      ) {
        state.quotationDetail.adultCount++;
      }
    },
    adultCountDecrement: (state) => {
      if (
        (state.quotationDetail?.planType == PlanType.Family ||
          state.quotationDetail?.planType == PlanType.Group) &&
        state.quotationDetail?.adultCount > FamilyCount.MIN_ADULT
      ) {
        state.quotationDetail.adultCount--;
      }
    },
    childCountIncrement: (state) => {
      if (
        state.quotationDetail?.planType == PlanType.Family &&
        state.quotationDetail?.childCount < FamilyCount.MAX_CHILD
      ) {
        state.quotationDetail.childCount++;
      }
    },
    childCountDecrement: (state) => {
      if (
        state.quotationDetail?.planType == PlanType.Family &&
        state.quotationDetail?.childCount > FamilyCount.MIN_CHILD
      ) {
        state.quotationDetail.childCount--;
      }
    },
    updatePayMode: (state, action: PayloadAction<string>) => {
      state.payMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(calculateTotalPremium.fulfilled, (state, action) => {
      state.totalPremium = action.payload.totalPremium;
      state.discountPremium = action.payload.discountPremium;
    });

    builder
      .addCase(getTravelPlanPrice.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTravelPlanPrice.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(getTravelPlanPrice.rejected, (state) => {
        state.loading = false;
      });

    builder.addCase(getTravelPlanPriceBreakdown.fulfilled, (state, action) => {
      state.loading = false;
      state.priceBreakdown = action.payload.data as PriceBreakdownResponse;
    });

    builder.addCase(calculatePremium.fulfilled, (state, action) => {
      state.loading = false;
      state.priceBreakdown = action.payload.data as PriceBreakdownResponse;
    });

    builder
      .addCase(retrieveTravelProposal.pending, (state) => {
        state.loading = true;
        state.proposalData = {
          message: '',
        } as ApiResponseWrapper<ProposalResponse>;
      })
      .addCase(retrieveTravelProposal.fulfilled, (state, action) => {
        state.loading = false;
        const proposalData =
          action.payload as ApiResponseWrapper<ProposalResponse>;
        state.proposalData = proposalData;

        if (proposalData.succeeded)
          state.encodedOrderId = proposalData.data.encodedOrderId;
      })
      .addCase(retrieveTravelProposal.rejected, (state, action) => {
        state.loading = false;
        state.proposalData =
          action.payload as ApiResponseWrapper<ProposalResponse>;
      });

    builder.addCase(createTravelProposal.fulfilled, (state, action) => {
      state.encodedOrderId = action.payload.data;
    });

    builder
      .addCase(getProductConfigList.pending, (state, action) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getProductConfigList.fulfilled, (state, action) => {
        state.productConfig = (action.payload.data as ProductConfigResponse[])
          .map(({ key, value }) => ({ [key]: value }))
          .reduce((prev, curr) => ({ ...prev, ...curr }), {});

        state.loading = false;
        state.error = false;
        // }
      })
      .addCase(getProductConfigList.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
      });
  },
});

export const {
  updateTripType,
  updateAddonDetail,
  updateSelectedPricingPlan,
  updateQuotationDetail,
  updateFlightDetail,
  updateDeclaration,
  updateTermsAndCondition,
  updateMarketingConsent,
  updateBankAccountDetail,
  setAsReadyForPayment,
  mapProposalDataToPlanSummary,
  adultCountIncrement,
  adultCountDecrement,
  childCountIncrement,
  childCountDecrement,
  updatePayMode,
  updateFlightDetails,
} = planSummarySlice.actions;

export default planSummarySlice.reducer;
